// ABOUTME: Single source of truth for admin config PATCH-allowed fields and update logic.
import { EntityType } from './enums';
import { z } from 'zod';

type FieldApplyResult =
  | { ok: true; value: unknown }
  | { ok: false; error: string; statusCode: number };

type ConfigPatchFieldDef = {
  schema: z.ZodOptional<z.ZodTypeAny>;
  apply: (value: unknown) => FieldApplyResult;
};

const booleanField = (): ConfigPatchFieldDef => ({
  schema: z.boolean().optional(),
  apply: (value) => ({ ok: true, value }),
});

// Trims text; empty after trim is allowed (optional free-form fields).
const stringField = (): ConfigPatchFieldDef => ({
  schema: z.string().optional(),
  apply: (value) => ({ ok: true, value: (value as string).trim() }),
});

// Identity strings that workflows and pairing read — reject empty after trim.
const nonEmptyStringField = (fieldName: string): ConfigPatchFieldDef => ({
  schema: z.string().optional(),
  apply: (value) => {
    const trimmed = (value as string).trim();
    if (trimmed.length === 0) {
      return {
        ok: false,
        error: `${fieldName} must not be empty`,
        statusCode: 400,
      };
    }
    return { ok: true, value: trimmed };
  },
});

// Choices mirror the field registry `options` for the same key.
const enumField = (values: [string, ...string[]]): ConfigPatchFieldDef => ({
  schema: z.enum(values).optional(),
  apply: (value) => ({ ok: true, value }),
});

const stringArrayField = (): ConfigPatchFieldDef => ({
  schema: z.array(z.string()).optional(),
  apply: (value) => ({ ok: true, value }),
});

export const CONFIG_PATCH_FIELD_MAP: Record<string, ConfigPatchFieldDef> = {
  isDisabled: booleanField(),
  useTransmormer: booleanField(),
  useReleaseDate: booleanField(),
  useATASpec: booleanField(),
  skipUnlinkedTechlogFlights: booleanField(),
  useSyncRecordAdapter: {
    schema: z.boolean().optional(),
    apply: (value) => ({ ok: true, value: value === true }),
  },
  preflightCustomFields: stringArrayField(),
  aircraftRegistrations: {
    schema: z.array(z.string()).optional(),
    apply: (value) => {
      const registrations = value as string[];
      const cleaned = Array.from(
        new Set(
          registrations
            .map((registration) => registration.trim())
            .filter((registration) => registration.length > 0),
        ),
      );

      if (cleaned.length > 500) {
        return {
          ok: false,
          error: 'aircraftRegistrations exceeds 500 entries',
          statusCode: 400,
        };
      }

      return { ok: true, value: cleaned };
    },
  },

  // Operator/user identities (shared across paths)
  originOperatorId: nonEmptyStringField('originOperatorId'),
  originUserId: nonEmptyStringField('originUserId'),
  destinationOperatorId: nonEmptyStringField('destinationOperatorId'),
  destinationUserId: nonEmptyStringField('destinationUserId'),
  customRegistrationCode: stringField(),

  // TechLog → AMOS flags
  useICAOCode: booleanField(),
  hideComments: booleanField(),
  disableDeferrals: booleanField(),
  useThreePartAtaFormat: booleanField(),
  includeSubsubsection: booleanField(),
  ignoreInspectionComplete: booleanField(),
  ignoreInspectionCompleteSignature: booleanField(),
  useResolutionAirport: booleanField(),
  operatorShortName: stringField(),
  issueSignee: stringField(),
  amosUserSign: stringField(),
  ataChapterDefault: stringField(),
  ataSectionDefault: stringField(),
  ataSubjectDefault: stringField(),
  iataCode: stringField(),
  fluidTypes: stringArrayField(),

  // Leon → TechLog flags. Legacy groupByDate is not directly PATCH-able;
  // applyConfigPatchUpdates writes it whenever creationMethod changes so the
  // non-V2 reader cannot override an admin Creation Method edit.
  creationMethod: enumField(['standard', 'groupByDate', '1trip:1flight']),
  addCallsign: booleanField(),
  useV2: booleanField(),

  // TechLog → Leon flags
  closeJourneyLog: enumField(['open', 'closed']),
  defaultFuelUpliftToZero: booleanField(),
  useTruckActualForFuelUplift: booleanField(),

  // AMOS → TechLog flags
  convertWorkpackageToTask: booleanField(),

  // Transformation engine
  useSchema: booleanField(),

  // Aircraft scoping
  aircraftRegistration: stringField(),
  aircraftIds: stringArrayField(),

  // FL3XX flags
  useStaging: booleanField(),
  baseAPIUrl: stringField(),
};

export const configPatchFieldsSchema = z.object(
  Object.fromEntries(
    Object.entries(CONFIG_PATCH_FIELD_MAP).map(([key, fieldDef]) => [
      key,
      fieldDef.schema,
    ]),
  ),
);

export type ConfigPatchFieldKey = keyof typeof CONFIG_PATCH_FIELD_MAP;

export type ConfigPatchFields = z.infer<typeof configPatchFieldsSchema>;

export const CONFIG_PATCH_FIELD_KEYS = Object.keys(
  CONFIG_PATCH_FIELD_MAP,
) as ConfigPatchFieldKey[];

const CONFIG_PATCH_FIELD_KEY_SET = new Set<ConfigPatchFieldKey>(
  CONFIG_PATCH_FIELD_KEYS,
);

export function isPersistableConfigField(
  key: string,
): key is ConfigPatchFieldKey {
  return CONFIG_PATCH_FIELD_KEY_SET.has(key as ConfigPatchFieldKey);
}

export type ApplyConfigPatchResult =
  | { ok: true; updateData: Record<string, unknown> }
  | { ok: false; error: string; statusCode: number };

function effectiveField(
  updateData: Record<string, unknown>,
  existing: Record<string, unknown>,
  key: string,
): unknown {
  return key in updateData ? updateData[key] : existing[key];
}

export function applyConfigPatchUpdates(
  updates: ConfigPatchFields,
  existing: Record<string, unknown> = {},
): ApplyConfigPatchResult {
  const updateData: Record<string, unknown> = {};

  for (const key of CONFIG_PATCH_FIELD_KEYS) {
    const value = updates[key as keyof ConfigPatchFields];
    if (value === undefined) {
      continue;
    }

    const fieldDef = CONFIG_PATCH_FIELD_MAP[key];
    const result = fieldDef.apply(value);
    if (!result.ok) {
      return {
        ok: false,
        error: result.error,
        statusCode: result.statusCode,
      };
    }

    updateData[key] = result.value;
  }

  // Non-V2 Leon readers still prefer legacy groupByDate over creationMethod.
  // Keep them aligned whenever Creation Method is the edited control.
  if (updateData.creationMethod !== undefined) {
    updateData.groupByDate = updateData.creationMethod === 'groupByDate';
  }

  // Same invariant as AMOS create: non-ATA defect exports need a signee.
  // issueSignee only applies to defect configs, so the rule is defect-scoped;
  // flight/uplift configs legitimately run with useATASpec off and no signee.
  const useATASpec = effectiveField(updateData, existing, 'useATASpec');
  if (useATASpec === false && existing.entity === EntityType.DEFECT) {
    const issueSignee = effectiveField(updateData, existing, 'issueSignee');
    const trimmed = typeof issueSignee === 'string' ? issueSignee.trim() : '';
    if (!trimmed) {
      return {
        ok: false,
        error: 'issueSignee is required when useATASpec is false',
        statusCode: 400,
      };
    }
  }

  return { ok: true, updateData };
}
